// Branding toko (white-label, mig 196) — nama toko + logo + ikon PWA yang bisa
// diganti dari Pengaturan Website tanpa menyentuh kode. Helper murni tanpa
// Supabase/DOM supaya mudah diuji (pola siteSettings.ts).
//
// Sumber kebenaran branding = `site_settings`:
// - `store_name`      → nama toko (sudah ada sejak mig 083)
// - `store_logo_url`  → logo tampilan (login/daftar/banner install)
// - `store_pwa_icons` → JSON PwaIcons: ikon PWA hasil generate dari logo
//   (dipakai manifest dinamis + apple-touch-icon → ikon home screen & splash
//   screen Android). Kosong → fallback ikon bawaan di /icons/*.png.

import { CATALOG_MANIFEST_ICONS } from "@/lib/catalogIcons";

export type PwaIcons = {
  /** PNG persegi 192×192 (ikon PWA purpose "any"). */
  i192: string;
  /** PNG persegi 512×512 (ikon "any" + dasar splash screen Android). */
  i512: string;
  /** PNG maskable 512×512 (logo di safe-zone 80%, latar penuh). */
  mask512: string;
  /** PNG 180×180 untuk apple-touch-icon (latar solid, tanpa alpha). */
  apple180: string;
};

function isHttpUrl(value: unknown): value is string {
  return typeof value === "string" && /^https?:\/\//i.test(value.trim());
}

/** Parse nilai `store_pwa_icons` (JSON) → PwaIcons; bentuk tak valid → null. */
export function parsePwaIcons(raw: string | null | undefined): PwaIcons | null {
  if (!raw || !raw.trim()) return null;
  try {
    const obj = JSON.parse(raw) as Record<string, unknown>;
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) return null;
    const { i192, i512, mask512, apple180 } = obj;
    if (
      !isHttpUrl(i192) ||
      !isHttpUrl(i512) ||
      !isHttpUrl(mask512) ||
      !isHttpUrl(apple180)
    ) {
      return null;
    }
    return {
      i192: i192.trim(),
      i512: i512.trim(),
      mask512: mask512.trim(),
      apple180: apple180.trim(),
    };
  } catch {
    return null;
  }
}

/** Serialisasi PwaIcons → string JSON untuk disimpan di `site_settings`. */
export function serializePwaIcons(icons: PwaIcons): string {
  return JSON.stringify({
    i192: icons.i192,
    i512: icons.i512,
    mask512: icons.mask512,
    apple180: icons.apple180,
  });
}

/**
 * Nama pendek aplikasi (manifest `short_name` / judul ikon home screen).
 * Rekomendasi PWA ≤ 12 karakter: nama ≤ 12 dipakai apa adanya; lebih panjang →
 * kata pertama (bila muat), terakhir dipotong 12 karakter.
 */
export function shortAppName(storeName: string): string {
  const name = storeName.trim().replace(/\s+/g, " ");
  if (!name) return "Toko";
  if (name.length <= 12) return name;
  const first = name.split(" ")[0];
  if (first.length > 0 && first.length <= 12) return first;
  return name.slice(0, 12).trim();
}

/** Inisial 1 huruf untuk avatar/ikon fallback saat logo belum diunggah. */
export function brandInitialLetter(storeName: string): string {
  const ch = storeName.trim().charAt(0);
  return ch ? ch.toUpperCase() : "T";
}

/** Tipe file logo yang diterima form upload (diproses canvas → PNG). */
export const LOGO_ACCEPT_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/svg+xml",
] as const;

export const MAX_LOGO_BYTES = 2 * 1024 * 1024; // 2 MB

/** Validasi file logo dari form admin → pesan error atau null bila valid. */
export function validateLogoUpload(file: {
  type: string;
  size: number;
}): string | null {
  if (!(LOGO_ACCEPT_TYPES as readonly string[]).includes(file.type)) {
    return "Format logo harus PNG, JPG, WEBP, atau SVG.";
  }
  if (file.size > MAX_LOGO_BYTES) return "Ukuran logo maksimal 2 MB.";
  return null;
}

/** Path Storage untuk aset branding di bucket publik `product-media`. */
export function brandingAssetPath(
  kind: string,
  ext: string,
  ts: number
): string {
  return `branding/${kind}-${ts}.${ext}`;
}

// ── Manifest PWA dinamis ─────────────────────────────────────────────────────
// Cermin manifest statis lama (public/manifest.*.webmanifest) tapi nama, ikon,
// dan (sejak Fase 3 audit warna) `theme_color` mengikuti setelan toko.

export type ManifestIconEntry = {
  src: string;
  sizes: string;
  type: string;
  purpose?: string;
};

/** Prefix ikon bawaan per varian aplikasi (fallback saat logo belum diunggah). */
const FALLBACK_ICON = {
  customer: {
    i192: "/icons/icon-192.png",
    i512: "/icons/icon-512.png",
    mask192: "/icons/maskable-192.png",
    mask512: "/icons/maskable-512.png",
  },
  admin: {
    i192: "/icons/admin-icon-192.png",
    i512: "/icons/admin-icon-512.png",
    mask192: "/icons/admin-maskable-192.png",
    mask512: "/icons/admin-maskable-512.png",
  },
} as const;

export function manifestIcons(
  icons: PwaIcons | null,
  variant: "customer" | "admin"
): ManifestIconEntry[] {
  if (icons) {
    return [
      { src: icons.i192, sizes: "192x192", type: "image/png", purpose: "any" },
      { src: icons.i512, sizes: "512x512", type: "image/png", purpose: "any" },
      {
        src: icons.mask512,
        sizes: "512x512",
        type: "image/png",
        purpose: "maskable",
      },
    ];
  }
  const fb = FALLBACK_ICON[variant];
  return [
    { src: fb.i192, sizes: "192x192", type: "image/png", purpose: "any" },
    { src: fb.i512, sizes: "512x512", type: "image/png", purpose: "any" },
    { src: fb.mask192, sizes: "192x192", type: "image/png", purpose: "maskable" },
    { src: fb.mask512, sizes: "512x512", type: "image/png", purpose: "maskable" },
  ];
}

function shortcutIcon(icons: PwaIcons | null, variant: "customer" | "admin") {
  return [
    {
      src: icons ? icons.i192 : FALLBACK_ICON[variant].i192,
      sizes: "192x192",
      type: "image/png",
    },
  ];
}

/**
 * Warna manifest bawaan (pink Galea lama) — dipakai HANYA bila pemanggil tak
 * meneruskan warna tema. Lihat `buildCustomerManifest`/`buildAdminManifest`.
 */
export const DEFAULT_MANIFEST_THEME_COLOR = "#F36B93";
export const DEFAULT_ADMIN_MANIFEST_THEME_COLOR = "#9F3457";

/**
 * Manifest PWA Pelanggan (cermin manifest.customer.webmanifest lama).
 *
 * `themeColor` opsional = WARNA TEMA toko (Editor Tampilan). Manifest sudah
 * dinamis untuk nama & ikon sejak mig 196, tapi warnanya dulu hardcoded pink
 * lama sehingga splash screen Android & title bar aplikasi ter-install tak
 * pernah ikut tema (docs/audit-warna-tema.md §P7). Dibiarkan OPSIONAL supaya
 * pemanggil yang tak punya konteks tema tetap dapat perilaku lama.
 */
export function buildCustomerManifest(
  storeName: string,
  icons: PwaIcons | null,
  themeColor?: string | null
): Record<string, unknown> {
  const name = storeName.trim() || "Toko";
  return {
    id: "/?app=customer",
    name,
    short_name: shortAppName(name),
    description: `Belanja preorder & ready stock ${name}`,
    lang: "id",
    start_url: "/",
    scope: "/",
    display: "standalone",
    orientation: "portrait",
    theme_color: (themeColor ?? "").trim() || DEFAULT_MANIFEST_THEME_COLOR,
    background_color: "#FFF9FA",
    icons: manifestIcons(icons, "customer"),
    shortcuts: [
      {
        name: "Keranjang Pesanan",
        short_name: "Keranjang",
        url: "/orders",
        icons: shortcutIcon(icons, "customer"),
      },
      {
        name: "Tagihan",
        short_name: "Tagihan",
        url: "/tagihan",
        icons: shortcutIcon(icons, "customer"),
      },
      {
        name: "Saldo",
        short_name: "Saldo",
        url: "/saldo",
        icons: shortcutIcon(icons, "customer"),
      },
    ],
  };
}

/** Manifest PWA Katalog Reseller (`/toko/[kode]/manifest.webmanifest`). */
export type ResellerManifest = {
  id: string;
  name: string;
  short_name: string;
  description: string;
  lang: string;
  start_url: string;
  scope: string;
  display: "standalone";
  icons: ManifestIconEntry[];
};

/**
 * Manifest PWA untuk katalog reseller `/toko/<kode>` — NETRAL, tanpa merek toko
 * induk. Reseller yang "Add to Home Screen" dari katalognya mendapat aplikasi
 * bernama DIA yang membuka katalognya sendiri (`start_url` + `scope` di-pin ke
 * `/toko/<kode>`), bukan beranda toko induk. Ikon SELALU lambang katalog
 * `/icons/katalog-*` — sengaja TIDAK membaca `site_settings` (`store_pwa_icons`
 * berisi logo toko induk; memakainya = bocor merek ke PWA reseller).
 */
export function buildResellerManifest(input: {
  kode: string;
  /** Nama reseller dari get_public_reseller_catalog; kosong = katalog nonaktif/anonim. */
  nama: string;
}): ResellerManifest {
  const nama = input.nama.trim().replace(/\s+/g, " ");
  const path = `/toko/${input.kode}`;
  return {
    id: path,
    name: nama ? `${nama} · Katalog` : "Katalog",
    // `shortAppName("")` jatuh ke "Toko" — utk katalog anonim label netralnya
    // "Katalog", jadi fallback ditangani di sini, bukan di shortAppName.
    short_name: nama ? shortAppName(nama) : "Katalog",
    description: nama ? `Katalog ${nama}` : "Katalog",
    lang: "id",
    start_url: path,
    scope: path,
    display: "standalone",
    // Lambang KATALOG (etalase biru platform, `catalogIcons.ts`) — tanpa fetch
    // branding. Dulu set storefront oranye `/icons/icon-*` yang terbaca gembok.
    icons: CATALOG_MANIFEST_ICONS,
  };
}

/**
 * Manifest PWA Admin (cermin manifest.admin.webmanifest lama). `themeColor`
 * opsional — lihat catatan di `buildCustomerManifest`.
 */
export function buildAdminManifest(
  storeName: string,
  icons: PwaIcons | null,
  themeColor?: string | null
): Record<string, unknown> {
  const name = storeName.trim() || "Toko";
  return {
    id: "/admin?app=admin",
    name: `${name} Admin`,
    short_name: `${shortAppName(name)} Admin`,
    description: `Panel admin backend ${name}`,
    lang: "id",
    start_url: "/admin",
    scope: "/admin",
    display: "standalone",
    theme_color: (themeColor ?? "").trim() || DEFAULT_ADMIN_MANIFEST_THEME_COLOR,
    background_color: "#FFF9FA",
    icons: manifestIcons(icons, "admin"),
    shortcuts: [
      {
        name: "Pesanan",
        short_name: "Pesanan",
        url: "/admin/orders",
        icons: shortcutIcon(icons, "admin"),
      },
      {
        name: "Tagihan",
        short_name: "Tagihan",
        url: "/admin/invoices",
        icons: shortcutIcon(icons, "admin"),
      },
      {
        name: "Stok",
        short_name: "Stok",
        url: "/admin/stok",
        icons: shortcutIcon(icons, "admin"),
      },
    ],
  };
}
